import React from 'react';
import { useUserInfo } from '../../../hooks/useUserPermissions';
import { useLexChatStore } from './lexChatStore';
import { UniversalLexAssistant } from '../../../components/lex/UniversalLexAssistant';
import { LexContext } from '../../../services/universalLexClient';
import { MatterDetailed } from '../../../types/matters';

interface LexAssistantWidgetProps {
  currentMatter?: MatterDetailed | null;
  currentContact?: any;
  containerMode?: 'floating' | 'sidebar';
  /** Explicit module override — use when the host page identity differs from entity presence. */
  module?: LexContext['application'];
}

/**
 * Matters Lex Assistant Widget - Now powered by UniversalLexAssistant
 * 
 * This component serves as a wrapper around the UniversalLexAssistant
 * specifically for the Matters module. It maintains the same interface
 * as the original widget but now uses the unified Lex system.
 */
export const LexAssistantWidget: React.FC<LexAssistantWidgetProps> = ({
  currentMatter,
  currentContact,
  containerMode = 'floating',
  module,
}) => {
  const userInfo = useUserInfo();
  const userName = userInfo?.firstName || userInfo?.fullName || 'User';
  
  // Local store for UI state (keeping for backward compatibility)
  const {
    isLexOpen,
    isLexPinned,
    setLexOpen,
    setLexPinned
  } = useLexChatStore();

  // Treat pinned as visible/open so users always retain panel controls.
  const isLexVisible = isLexOpen || isLexPinned;

  // Determine the application context: explicit module prop > entity detection > default
  const getApplicationContext = (): LexContext['application'] => {
    if (module) return module;
    if (currentContact) return 'contacts';
    if (currentMatter) return 'matters';
    return 'matters';
  };

  // Build context for Universal Lex Assistant
  const lexContext: LexContext = {
    application: getApplicationContext(),
    matterId: currentMatter?.id,
    matterLabel: currentMatter?.description || currentMatter?.title || undefined,
    clientId: currentMatter?.client_id || currentContact?.id,
    clientName: currentContact?.name || undefined,
    userName: userName,
    currentView: currentMatter ? 'matter-detail' : currentContact ? 'contact-detail' : `${getApplicationContext()}-list`,
    pluginIds: []
  };

  // Determine variant based on container mode
  const variant = containerMode === 'sidebar' ? 'sidebar' : 'modal';

  // Close behavior must never leave a pinned-but-unopen dead state.
  const handleToggleOpen = () => {
    if (isLexVisible) {
      if (isLexPinned) {
        setLexPinned(false);
      }
      setLexOpen(false);
      return;
    }
    setLexOpen(true);
  };

  return (
    <UniversalLexAssistant
      context={lexContext}
      isOpen={isLexVisible}
      isPinned={isLexPinned}
      onToggleOpen={handleToggleOpen}
      onTogglePin={() => setLexPinned(!isLexPinned)}
      variant={variant}
      className={containerMode === 'sidebar' ? 'w-full' : ''}
    />
  );
};

export default LexAssistantWidget;
